import ReactGA from "react-ga4";
import googleAdsConversions from "../config/googleAdsConversions.json";

// All Google Ads conversion config lives in one file:
//   client/src/config/googleAdsConversions.json
//   { "id": "AW-123456789", "conversions": { "<name>": "<label>" } }
// To add a conversion: create it in Google Ads (Goals > Conversions > New >
// Website, "Use the Google tag"), which gives you `send_to: 'AW-123/AbCdEf'`.
// Set "id" to the AW- part (once) and add a "<name>": "<label>" entry under
// "conversions". The shared "id" is used for every conversion.
export const GOOGLE_ADS_ID = googleAdsConversions.id;

// Marketing tags (GA4, Google Ads, Meta Pixel) must only ever load on the real
// production hosts. CRA sets NODE_ENV=production for EVERY production build,
// staging included, so gating on NODE_ENV alone sent staging page views and
// internal CRM route paths into the production GA4 property and Ads account.
// The environment has to be identified explicitly.
const PRODUCTION_HOST = "abundowealth.com";

/**
 * True only for the production hosts: the apex domain, www, and every firm
 * subdomain (acme.abundowealth.com). Staging (*.ondigitalocean.app) and
 * localhost never match.
 */
export const isProductionAnalyticsHost = (hostname = "") => {
    const host = String(hostname).toLowerCase();
    return host === PRODUCTION_HOST || host.endsWith(`.${PRODUCTION_HOST}`);
};

/**
 * Whether the marketing tags may load in this environment.
 *
 * REACT_APP_ENABLE_ANALYTICS is an explicit override ("true" enables, anything
 * else disables) so a preview host can opt in, or production can opt out,
 * without a code change. With the override unset we fall back to the hostname,
 * so production keeps working with no app-spec change required.
 */
export const analyticsEnabled = () => {
    const override = process.env.REACT_APP_ENABLE_ANALYTICS;
    if (override !== undefined && override !== "") return override === "true";

    if (typeof window === "undefined" || !window.location) return false;
    return (
        process.env.NODE_ENV === "production" &&
        isProductionAnalyticsHost(window.location.hostname)
    );
};

/**
 * Fire a Google Ads conversion by name (a key under "conversions" in
 * googleAdsConversions.json). Piggybacks on the gtag.js instance react-ga4
 * already loads, so no extra script is needed.
 */
export const trackAdsConversion = (name, params = {}) => {
    const label = googleAdsConversions.conversions[name];
    if (!label) return;
    try {
        ReactGA.gtag("event", "conversion", {
            send_to: `${GOOGLE_ADS_ID}/${label}`,
            ...params,
        });
    } catch (error) {
        console.error("Google Ads conversion tracking error:", error);
    }
};

/**
 * Track a custom event in Google Analytics
 * @param {string} label - Event label/identifier
 * @param {object} params - Additional event parameters (optional)
 */
export const trackEvent = (label = "", params = {}) => {
    try {
        ReactGA.event({
            action: "scheduled_discovery",
            category: "engagement",
            label: label,
            value: 1546,
        });
    } catch (error) {
        console.error("Analytics tracking error:", error);
    }
};

/**
 * Fire a named GA4 event with arbitrary params. Unlike the legacy `trackEvent`
 * above (which always sends the same action), this sends a real GA4 event name
 * so it can be configured as a conversion in Google Analytics and imported into
 * Google Ads. Never throws — analytics must never break the page.
 *
 * @param {string} eventName - GA4 event name (snake_case), e.g. 'get_started_submit'
 * @param {object} [params] - Event parameters (scalar values only)
 */
export const trackGAEvent = (eventName = "", params = {}) => {
    try {
        if (!eventName) return;
        ReactGA.event(eventName, params);
    } catch (error) {
        console.error("Analytics tracking error:", error);
    }
};

/**
 * Track a login event in Google Analytics
 */
export const trackLogin = () => {
    ReactGA.event({
        action: "login",
        category: "engagement",
        label: "login",
    });
};

